"use client";

// src/components/storefront/structured-widgets/ecommerce/CarouselWrapper.tsx
//
// Pure Tailwind CSS carousel. The prev/next controls live on the section's
// heading row, at its far end, as a matching pair of outlined circles — they
// are part of the section chrome rather than an overlay on the cards.
// RTL-aware: the row order, the caret direction and the scroll direction all
// flip automatically.

import { IconButton } from "@/components/storefront/ui/icon-button";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";
import { useEffect, useRef, useState } from "react";

type CarouselWrapperProps = {
  children: React.ReactNode;
  /** Tailwind width class per item. Default: "w-[calc(50%-8px)] md:w-[calc(33.333%-11px)] lg:w-[calc(25%-12px)]" */
  itemClass?: string;
  /** Heading block. Rendered at the start of the controls row. Pass it without
   *  an outer margin — the wrapper owns the spacing below the row. */
  header?: React.ReactNode;
  /** "center" keeps a centered header full-width and drops the controls onto
   *  their own row beneath it, since a centered heading cannot share the row. */
  headerAlign?: "start" | "center";
  locale?: string;
};

export function CarouselWrapper({
  children,
  itemClass,
  header,
  headerAlign = "start",
  locale,
}: CarouselWrapperProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  // Controls are only drawn once the track actually overflows. A row of four
  // cards in a four-up carousel has nowhere to scroll, and arrows that do
  // nothing read as a broken section.
  const [overflowing, setOverflowing] = useState(false);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;
    const measure = () =>
      setOverflowing(container.scrollWidth - container.clientWidth > 1);
    measure();
    // The track is observed for viewport changes and the first card for the
    // reflow that lands when its image finally resolves.
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    if (container.firstElementChild) {
      observer.observe(container.firstElementChild);
    }
    return () => observer.disconnect();
  }, [children]);

  function scroll(direction: "prev" | "next") {
    const container = scrollRef.current;
    if (!container) return;
    const styles = getComputedStyle(container);
    const isRtl = styles.direction === "rtl";

    // Slide by the number of items currently visible in the viewport so a
    // click reveals the next full page of items instead of an arbitrary
    // fraction of the container.
    const firstItem = container.firstElementChild as HTMLElement | null;
    const itemWidth = firstItem?.getBoundingClientRect().width ?? 0;
    const gap =
      parseFloat(styles.columnGap || styles.gap || "0") || 0;
    const stride = itemWidth + gap;
    const visibleItems =
      stride > 0
        ? Math.max(1, Math.floor((container.clientWidth + gap) / stride))
        : 1;
    const scrollAmount = stride > 0 ? visibleItems * stride : container.clientWidth;
    const left =
      direction === "next"
        ? isRtl
          ? -scrollAmount
          : scrollAmount
        : isRtl
          ? scrollAmount
          : -scrollAmount;

    // Check if at the end → loop to start, or at start → loop to end
    const atEnd = isRtl
      ? container.scrollLeft <=
        -container.scrollWidth + container.clientWidth + 10
      : container.scrollLeft >=
        container.scrollWidth - container.clientWidth - 10;
    const atStart = isRtl
      ? container.scrollLeft >= -10
      : container.scrollLeft <= 10;

    if (direction === "next" && atEnd) {
      container.scrollTo({ left: 0, behavior: "smooth" });
      return;
    }
    if (direction === "prev" && atStart) {
      const end = isRtl
        ? -(container.scrollWidth - container.clientWidth)
        : container.scrollWidth - container.clientWidth;
      container.scrollTo({ left: end, behavior: "smooth" });
      return;
    }

    container.scrollBy({ left, behavior: "smooth" });
  }

  const isAr = locale === "ar";
  // The carets are authored for LTR and flipped by the `dir` on <html> rather
  // than by a runtime direction check, so they point the right way on the
  // server render too.
  const controls = overflowing ? (
    <div className="flex items-center gap-2 shrink-0">
      <IconButton
        variant="outline"
        color="default"
        size="lg"
        shape="rounded"
        icon={<CaretLeft weight="bold" className="rtl:rotate-180" />}
        onClick={() => scroll("prev")}
        aria-label={isAr ? "السابق" : "Previous"}
      />
      <IconButton
        variant="outline"
        color="default"
        size="lg"
        shape="rounded"
        icon={<CaretRight weight="bold" className="rtl:rotate-180" />}
        onClick={() => scroll("next")}
        aria-label={isAr ? "التالي" : "Next"}
      />
    </div>
  ) : null;

  return (
    <div className="relative">
      {headerAlign === "center" ? (
        <>
          {header && <div className="mb-4">{header}</div>}
          {controls && <div className="flex justify-end mb-4">{controls}</div>}
        </>
      ) : (
        (header || controls) && (
          <div className="mb-6 flex items-center justify-between gap-4">
            <div className="min-w-0 flex-1">{header}</div>
            {controls}
          </div>
        )
      )}

      {/* Scroll container */}
      <div
        ref={scrollRef}
        // overflow-y-hidden is load-bearing: `overflow-x: auto` alone computes
        // `overflow-y: auto` per spec, so the track grew a vertical scrollbar
        // the moment any card was a fraction taller than the flex line. The
        // track's height is the card's height and nothing may scroll in it.
        // py-2/-my-2 gives the cards' hover shadow room inside the clip box
        // without changing where the track sits in the flow.
        className="flex gap-4 overflow-x-auto overflow-y-hidden snap-x snap-mandatory scroll-smooth scrollbar-hide py-2 -my-2"
      >
        {Array.isArray(children) ? (
          children.map((child, i) => (
            <div
              key={i}
              className={`snap-start shrink-0 ${itemClass ?? "w-[calc(50%-8px)] md:w-[calc(33.333%-11px)] lg:w-[calc(25%-12px)]"}`}
            >
              {child}
            </div>
          ))
        ) : (
          <div
            className={`snap-start shrink-0 ${itemClass ?? "w-[calc(50%-8px)] md:w-[calc(33.333%-11px)] lg:w-[calc(25%-12px)]"}`}
          >
            {children}
          </div>
        )}
      </div>
    </div>
  );
}
